import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  GET_ALL_CONTACTS,
  GET_MESSAGES,
  POST_MESSAGE,
  POST_START_CHAT,
  POST_REACTION,
  GET_LIST_OF_BLOCKED_USERS,
  POST_BLOCK_USERS,
  POST_RESET_UNREAD_COUNT,
  POST_UNBLOCK_USERS,
  INITIATE_CALL,
  RECEIVE_CALL,
  TERMINATE_CALL,
} from '../../../api/wca/wca.chat.service';

const getBlockedUsersStorageKey = (metadataid) => `wca-blocked-users-${metadataid}`;

const getStoredBlockedUsers = (metadataid) => {
  if (!metadataid || typeof localStorage === 'undefined') return {};

  try {
    return JSON.parse(localStorage.getItem(getBlockedUsersStorageKey(metadataid)) || '{}');
  } catch (error) {
    return {};
  }
};

const storeBlockedUserStatus = (metadataid, phone, isBlocked) => {
  if (!metadataid || !phone || typeof localStorage === 'undefined') return;

  const storedUsers = getStoredBlockedUsers(metadataid);
  storedUsers[phone] = isBlocked;
  localStorage.setItem(getBlockedUsersStorageKey(metadataid), JSON.stringify(storedUsers));
};

const mergeBlockedStatus = (metadataid, contacts = []) => {
  const storedUsers = getStoredBlockedUsers(metadataid);

  return contacts.map((contact) => ({
    ...contact,
    isOptOut:
      typeof contact?.isOptOut === 'boolean' ? contact.isOptOut : Boolean(storedUsers[contact?.phone]),
  }));
};

export const getAllContacts = createAsyncThunk(
  'sales_support/getAllContacts',
  async (
    { metadataid, pageNo = 0, limit = 30, startDate, endDate, filterData, globalFilterValue },
    thunkAPI,
  ) => {
    try {
      const contacts = await GET_ALL_CONTACTS(
        metadataid,
        pageNo,
        limit,
        startDate,
        endDate,
        filterData,
        globalFilterValue,
      );
      return {
        ...contacts,
        rows: mergeBlockedStatus(metadataid, contacts?.rows),
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const getAllMessages = createAsyncThunk(
  'sales_support/getAllMessages',
  async ({ metadataid, phone, lastMessageId = 0, limit = 30 }, thunkAPI) => {
    try {
      const messages = await GET_MESSAGES(metadataid, phone, lastMessageId, limit);
      return messages;
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const sendMessage = createAsyncThunk(
  'sales_support/sendMessage',
  async ({ metadataid, data }, thunkAPI) => {
    try {
      const response = await POST_MESSAGE(metadataid, data);
      return response; // contains message info or server response
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  },
);
export const startChat = createAsyncThunk(
  'sales_support/startChat',
  async ({ metadataid, phone, data }, thunkAPI) => {
    try {
      const response = await POST_START_CHAT(metadataid, phone, data);
      return response; // contains message info or server response
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const sendReaction = createAsyncThunk(
  'sales_support/sendReaction',
  async ({ metadataid, data }, thunkAPI) => {
    try {
      const response = await POST_REACTION(metadataid, data);
      return response;
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const resetUnreadCount = createAsyncThunk(
  'sales_support/resetUnreadCount',
  async ({ metadataid, phone }, thunkAPI) => {
    try {
      const response = await POST_RESET_UNREAD_COUNT(metadataid, phone);
      return response;
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const blockChatUser = createAsyncThunk(
  'sales_support/blockChatUser',
  async ({ metadataid, phone }, thunkAPI) => {
    try {
      await POST_BLOCK_USERS(metadataid, { phoneNumbers: [phone] });
      storeBlockedUserStatus(metadataid, phone, true);
      return { phone, blockStatus: 'block' };
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const unblockChatUser = createAsyncThunk(
  'sales_support/unblockChatUser',
  async ({ metadataid, phone }, thunkAPI) => {
    try {
      await POST_UNBLOCK_USERS(metadataid, { phoneNumbers: [phone] });
      storeBlockedUserStatus(metadataid, phone, false);
      return { phone, blockStatus: 'unblock' };
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  },
);
export const getBlockedUsers = createAsyncThunk(
  'sales_support/getBlockedUsers',
  async ({ metadataid }, thunkAPI) => {
    try {
      const blockedUsers = await GET_LIST_OF_BLOCKED_USERS(metadataid);
      return blockedUsers;
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  },
);


export const initiateCall = createAsyncThunk(
  'sales_support/initiateCall',
  async ({ metadataid, phone, data = null, page = 0, limit = 10 }, thunkAPI) => {
    try {
      return await INITIATE_CALL(metadataid, phone, data, page, limit);
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  },
);
export const receiveCall = createAsyncThunk(
  'sales_support/receiveCall',
  async ({ metadataid, phone, data }, thunkAPI) => {
    try {
      return await RECEIVE_CALL(metadataid, phone, data);
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err?.response?.data || err.message
      );
    }
  }
);


export const terminateCall = createAsyncThunk(
  'sales_support/terminateCall',
  async ({ metadataid, phone, data }, thunkAPI) => {
    try {
      return await TERMINATE_CALL(metadataid, phone, data);
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err?.response?.data || err.message
      );
    }
  }
);


